import React, { useState } from "react";
import styled from "styled-components";
import { useDroppable } from "@dnd-kit/core";
import { colors, logEducatorDashboard } from "utils";
import { trackClickedButton } from "utils/analytics";
import { Tooltip } from "components/shared/Tooltip/Tooltip";
import { resolveGroupCardTitle } from "utils/groupCardTitle";
import { KebabMenu } from "./KebabMenu";

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  padding: 0;
`;

const CodeText = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 20px;
  text-transform: uppercase;
`;

// "1 Learning Component" / "5 Learning Components", right of the standard code.
// The auto margin lives on the tooltip's wrapper span, which is what actually sits
// in the flex row.
const LcCountSlot = styled.span`
  display: inline-flex;
  margin-left: auto;
`;

const LcCount = styled.span`
  color: ${colors.nightlyWoods500};
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
`;

// Titles clamp to three lines per the design, which is what makes the hover text
// necessary in the first place.
const CardTitle = styled.span`
  color: ${colors.nightlyWoods500};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  flex: 1;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
  min-width: 0;
  overflow: hidden;
`;

const LcTipRow = styled.span`
  display: block;

  & + & {
    margin-top: 8px;
  }
`;

const LcTipLabel = styled.strong`
  font-weight: 700;
`;

const QuestionSetLink = styled.button`
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${colors.blue700};
  cursor: pointer;
  display: inline-flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  padding: 4px 0;
  text-decoration: underline;
`;

// Visual metadata per track. Shared with the accordion / add tile so colors stay
// consistent across the assignment editor. Keys match the API `track_type` enum.
export const TRACK_META = {
  core: {
    label: "Core",
    headerBg: colors.nightlyWoods500,
    tagBg: "#F2F2F2",
    tagBorder: "#ACAFAD",
    tagText: colors.nightlyWoods500,
  },
  prerequisites: {
    label: "Prerequisite",
    headerBg: colors.pink800,
    tagBg: colors.pink50,
    tagBorder: colors.pink300,
    tagText: colors.pink800,
  },
  extensions: {
    label: "Extension",
    headerBg: colors.green600,
    tagBg: colors.green50,
    tagBorder: "#76D9AD",
    tagText: colors.green700,
  },
  other: {
    label: "Other",
    headerBg: "#ba4a21",
    tagBg: "#fefce8",
    tagBorder: "#eab308",
    tagText: "#9b6b03",
  },
};

export const trackMeta = (trackType) => TRACK_META[trackType] || TRACK_META.core;

// Lucide "copy" — currentColor so it works on the card's dark header and on the
// white lesson header alike.
export const CopyIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Card footer icons (Figma Group Card/V2 action row). Focus = arrow into a
// circle; layers/heart mirror the Lucide glyphs the design references.
const FocusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g clipPath="url(#clip0_5381_8898)">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#524FC9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="#524FC9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="#524FC9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14.5 9.5L18.5 5.5" stroke="white" strokeWidth="4" />
      <path d="M22.2127 2.99989C22.6171 2.99989 22.9818 3.24363 23.1366 3.61723C23.2914 3.9909 23.2058 4.4209 22.9198 4.70689L20.8682 6.75847C20.6743 6.95222 20.4096 7.05771 20.1356 7.05057L18.1869 6.99947C17.6552 6.98547 17.2272 6.55747 17.2132 6.02581L17.1621 4.07713C17.155 3.80318 17.2605 3.53833 17.4542 3.34447L19.5058 1.29289C19.7917 1.00693 20.2218 0.921357 20.5954 1.07607C20.9691 1.23085 21.2128 1.59554 21.2128 2V2.99989H22.2127Z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 12L19 5" stroke="#524FC9" strokeWidth="2" strokeLinecap="round" />
      <path d="M20.2129 2L20.2129 4L22.2129 4L20.1616 6.05128L18.2129 6L18.1616 4.05128L20.2129 2Z" stroke="#524FC9" strokeWidth="2" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip0_5381_8898">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const LayersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2 2 7l10 5 10-5-10-5Z" />
    <path d="m2 12 10 5 10-5" />
    <path d="m2 17 10 5 10-5" />
  </svg>
);

const SmallHeartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
  </svg>
);

// Bottom action row: two equal-width stacked icon+label text buttons.
const ActionRow = styled.div`
  border-top: 1px solid #acafad;
  display: flex;
  margin-top: auto;
  max-height: 80px;
  padding: 16px 0;
  width: 100%;
`;

const ActionButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${colors.blue700};
  cursor: pointer;
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  line-height: 20px;
  min-width: 0;
  padding: 0 8px;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
`;

export const AssignmentGroupCard = ({
  assignmentId,
  // The GroupAssignmentOut row and the bundle it belongs to, verbatim — the helper
  // below turns them into every string this card renders.
  assignment,
  bundle,
  isEditing,
  onTrash,
  onEditStandard,
  onViewQuestions,
  onDuplicate,
  duplicateDisabled = false,
}) => {
  const groupCode = assignment?.group_code;
  const {
    code, codeTooltip, title, standard, countLabel, tooltipItems, trackType,
    showTrackTag, questionSetLabel: questionsLabel,
  } = resolveGroupCardTitle(assignment, bundle);
  const meta = trackMeta(trackType);
  const [copied, setCopied] = useState(false);
  const { setNodeRef, isOver } = useDroppable({
    id: `card:${assignmentId}:${groupCode}`,
    data: { kind: "card", assignmentId, standardCode: standard.code, standardName: standard.name, trackType },
  });

  // Copy the shareable student link (same as GroupCodeButton), not the bare code.
  const copyShareLink = () => {
    trackClickedButton("Copy Code", {
      page_name: "Dashboard",
      group_code: groupCode.toUpperCase(),
      card_type: "Assignment Group",
      standard_code: standard.code,
    });
    navigator.clipboard?.writeText(`${process.env.REACT_APP_OKOLABS_APP_URL}?code=${groupCode.toUpperCase()}`);
    logEducatorDashboard("Educator dashboard: Group code link copied", {
      event: "group_code_copied",
      groupCode,
    });
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        backgroundColor: "white",
        border: `1px solid ${isOver ? colors.blue500 : colors.blue400}`,
        borderRadius: 16,
        boxShadow: isOver ? `0 0 0 2px ${colors.blue300}` : "none",
        display: "flex",
        flexDirection: "column",
        // No overflow clip: the lesson-code tooltip sits beside the code and extends
        // past the card edge. The header rounds its own top corners instead.
      }}
    >
      {/* Colored header — Group Codes: CODE + (copy in read-only / remove in edit) */}
      <div style={{
        alignItems: "center",
        backgroundColor: meta.headerBg,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        color: "white",
        display: "flex",
        justifyContent: "space-between",
        padding: "12px 16px",
      }}>
        <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, lineHeight: "20px" }}>Group Codes:</span>
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.25px", lineHeight: "26px" }}>{groupCode?.toUpperCase()}</span>
          {!isEditing && (
            <CopyButton
              type="button"
              onClick={copyShareLink}
              aria-label={`Copy share link for group code ${groupCode}`}
              title={copied ? "Copied!" : "Copy group code link"}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </CopyButton>
          )}
        </div>
        {/* Edit mode collects the card's actions behind the kebab — Remove was a bare
            trash icon and Duplicate was read-mode only, so neither was discoverable
            where the teacher is actually editing. */}
        {isEditing && (onTrash || onDuplicate) && (
          <KebabMenu
            onDark
            ariaLabel={`Actions for group code ${groupCode}`}
            items={[
              ...(onDuplicate ? [{
                label: "Duplicate Group",
                onClick: onDuplicate,
                disabled: duplicateDisabled,
                title: duplicateDisabled ? "No group codes available" : undefined,
              }] : []),
              ...(onTrash ? [{
                label: "Delete Group",
                onClick: onTrash,
              }] : []),
            ]}
          />
        )}
      </div>

      {/* Body */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {/* Tag + standard code */}
          <div style={{ alignItems: "center", display: "flex", gap: 8 }}>
            {showTrackTag && (
              <span style={{
                backgroundColor: meta.tagBg,
                border: `1px solid ${meta.tagBorder}`,
                borderRadius: 11,
                color: meta.tagText,
                fontSize: 14,
                lineHeight: "20px",
                padding: "2px 8px",
                whiteSpace: "nowrap",
              }}>
                {meta.label}
              </span>
            )}
            <Tooltip
              id={`${groupCode}-code-tooltip`}
              tip={codeTooltip}
              placement="right"
              nowrap
            >
              <CodeText
                // Focusable only when it has a tooltip, so codes without one stay
                // out of the tab order.
                tabIndex={codeTooltip ? 0 : undefined}
                aria-describedby={codeTooltip ? `${groupCode}-code-tooltip` : undefined}
              >
                {code}
              </CodeText>
            </Tooltip>
            {/* Hovering the count reveals the full LC titles (Figma 4914-72908) —
                the title itself stays the lesson/standard name. */}
            {countLabel && (
              <LcCountSlot>
                <Tooltip
                  id={`${groupCode}-lc-tooltip`}
                  tip={tooltipItems.map(({ label, name }) => (
                    <LcTipRow key={label}>
                      <LcTipLabel>{label}</LcTipLabel> {name}
                    </LcTipRow>
                  ))}
                  placement="right"
                  maxWidth={280}
                >
                  <LcCount
                    tabIndex={0}
                    aria-describedby={`${groupCode}-lc-tooltip`}
                  >
                    {countLabel}
                  </LcCount>
                </Tooltip>
              </LcCountSlot>
            )}
          </div>
          {/* Name + view link, indented under the tag row (Figma Standard Header) */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <CardTitle>{title}</CardTitle>
            </div>
            {onViewQuestions && (
              <QuestionSetLink type="button" onClick={onViewQuestions}>
                {questionsLabel}
              </QuestionSetLink>
            )}
          </div>
        </div>
      </div>

      {/* Action row (Figma Group Card/V2), edit mode only: Edit Group Focus +
          Edit Questions Set. The heart marks a group already carrying a saved set.
          Edit Questions Set opens the same preview modal as the card's questions
          link — the modal's own CTA is the way into the Question Set page. */}
      {isEditing && (onEditStandard || onViewQuestions) && (
        <ActionRow>
          {onEditStandard && (
            <ActionButton type="button" onClick={onEditStandard}>
              <FocusIcon />
              Edit Group Focus
            </ActionButton>
          )}
          {onViewQuestions && (
            <ActionButton type="button" onClick={onViewQuestions}>
              {assignment?.question_set_id ? <SmallHeartIcon /> : <LayersIcon />}
              {assignment?.question_set_id ? "Edit My Questions Set" : "Edit Questions Set"}
            </ActionButton>
          )}
        </ActionRow>
      )}
    </div>
  );
};
